// src/pages/Modelo.jsx
import React, { useEffect, useState } from 'react';
import TagManager from 'react-gtm-module';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './Modelo.module.scss';
import Modal from '../components/Modal/Modal';
import Cotizador from '../components/Cotizador/Cotizador';
import Breadcrumb from '../components/Breadcrumb/Breadcrumb';
import ModeloSpecs from '../components/ModeloSpecs/ModeloSpecs';
import ModeloTable from '../components/ModeloTable/ModeloTable';
import PageFooter from '../components/PageFooter';
import ModeloHero from '../components/ModeloHero/ModeloHero';
import ModeloDescripcion from '../components/ModeloDescripcion/ModeloDescripcion';
import ModeloDestacado from '../components/ModeloDestacado/ModeloDestacado';

const Modelo = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [modelo, setModelo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCotizadorOpen, setCotizadorOpen] = useState(false);

  const handleOpenCotizador = () => setCotizadorOpen(true);
  const handleCloseCotizador = () => setCotizadorOpen(false);

  // Scroll to top on slug change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Fetch model data
  useEffect(() => {
    const fetchModelData = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/modelos?filters[slug][$eq]=${slug}&populate=*`
        );
        const data = await response.json();
        if (!response.ok || !data.data?.length) {
          throw new Error(`Error fetching model details for slug: ${slug}`);
        }
        const d = data.data[0];
        setModelo({
          documentId: d.documentId || null,
          nombre: d.nombre || 'Sin nombre',
          // Si no hay descripción, dejamos null
          descripcion: d.descripcion ?? null,
          slug: d.slug || null,
          serie: d.serie
            ? { documentId: d.serie.documentId, nombre: d.serie.nombre }
            : { documentId: null, nombre: 'Sin serie' },
          linea: d.linea
            ? { documentId: d.linea.documentId, nombre: d.linea.nombre }
            : { documentId: null, nombre: 'Sin línea' },
          imagenCardUrl:
            d.imagen_card?.formats?.medium?.url ||
            d.imagen_card?.formats?.small?.url ||
            d.imagen_card?.url ||
            '/images/default-banner.webp',
          especificaciones: (d.especificaciones || []).map((esp) => ({
            name: esp.name || 'Sin nombre',
            value: esp.value || 'Sin valor',
          })),
          keywords: d.keywords || '',
          createdAt: d.createdAt,
          updatedAt: d.updatedAt,
          publishedAt: d.publishedAt,
        });
      } catch (err) {
        console.error('Error fetching model data:', err);
        setError('Error loading model data.');
      } finally {
        setLoading(false);
      }
    };
    fetchModelData();
  }, [slug]);

  useEffect(() => {
    if (!modelo) return;
  
    if (typeof window !== 'undefined') {
      TagManager.dataLayer({
        dataLayer: {
          event: 'ux_event',            // disparador genérico
          event_name: 'page_view_model',// nombre real del evento
          event_category: 'page_view',
          model_name: modelo.nombre,
          model_slug: slug
        }
      });
    }
  }, [modelo, slug]);
  
  // Update document title & meta tags
  useEffect(() => {
    if (!modelo) return;
    document.title = `Zoomlion Latinoamérica | ${modelo.nombre}`;

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = modelo.descripcion || '';

    let metaKeys = document.querySelector('meta[name="keywords"]');
    if (!metaKeys) {
      metaKeys = document.createElement('meta');
      metaKeys.name = 'keywords';
      document.head.appendChild(metaKeys);
    }
    metaKeys.content = modelo.keywords.split(',').join(', ');
  }, [modelo]);

  if (loading) {
    return (
      <div className={styles.modeloPageLoading}>
        <div className="spinner">Cargando...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.modeloPageError}>
        <h2>Ocurrió un error</h2>
        <p>{error}</p>
        <button
          onClick={() => navigate(-1)}
          className={styles.modeloPageButton}
        >
          Volver
        </button>
      </div>
    );
  }

  if (!modelo) {
    return <div>Error al cargar los detalles del modelo.</div>;
  }

  const hasSpecs =
    Array.isArray(modelo.especificaciones) && modelo.especificaciones.length > 0;

  return (
    <>
      <div className={styles.modeloPageContainer}>
        {/* Breadcrumb */}
        <Breadcrumb linea={modelo.linea} serie={modelo.serie} />

        {/* Hero */}
        <ModeloHero modelo={modelo} onOpenCotizador={handleOpenCotizador} />

        {/* Descripción: solo si existe texto no vacío */}
        {modelo.descripcion && modelo.descripcion.trim() !== '' && (
          <ModeloDescripcion modelo={modelo} />
        )}

        {/* Especificaciones */}
        {hasSpecs && (
          <div className={styles.modeloPageDetail}>
            {modelo.especificaciones.length === 3 ? (
              <ModeloSpecs specs={modelo.especificaciones} />
            ) : (
              <>
                <ModeloSpecs specs={modelo.especificaciones} />
                <ModeloTable specs={modelo.especificaciones} />
              </>
            )}
          </div>
        )}

        {/* Destacado */}
        <ModeloDestacado linea={modelo.linea} />
      </div>

      {/* Footer */}
      <div className={styles.modeloPageFooter}>
        <PageFooter />
      </div>

      {/* Modal Cotizador */}
      <Modal isOpen={isCotizadorOpen} onClose={handleCloseCotizador}>
        <Cotizador
          currentCountry="Perú"
          modelo={modelo}
          onClose={handleCloseCotizador}
        />
      </Modal>
    </>
  );
};

export default Modelo;
